import { NewPatientForm } from "../types/supabase";
import { formatPhoneNumber } from "./format";

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export const createEmptyNewPatientForm = (): NewPatientForm => ({
  nome: "",
  sobrenome: "",
  data_x0020_de_x0020_nascimento: "",
  sexo: "",
  patientCpf: "",
  patientRg: "",
  patientEmail: "",
  patientPhone: "",
  altura_x0020__x0028_cm_x0029_: 0,
  peso: 0,
  diagn_x00f3_sticos: "",
  patientAddressStreet: "",
  patientAddressNumber: "",
  patientAddressComplement: "",
  patientAddressNeighborhood: "",
  patientAddressCity: "",
  patientAddressState: "",
  patientAddressZipCode: "",
  patientEmergencyContactName: "",
  patientEmergencyContactPhone: "",
  patientEmergencyContactRelationship: "",
  patientMedicalInfoBloodType: "",
  patientMedicalInfoAllergies: "",
  patientMedicalInfoMedications: "",
});

export const validateCpf = (cpf: string): boolean => {
  const normalizedCpf = cpf.replace(/[^\d]+/g, "");

  if (!normalizedCpf || normalizedCpf.length !== 11 || /^(\d)\1+$/.test(normalizedCpf)) {
    return false;
  }

  let sum = 0;
  for (let index = 0; index < 9; index += 1) {
    sum += Number(normalizedCpf.charAt(index)) * (10 - index);
  }

  let remainder = 11 - (sum % 11);
  if (remainder === 10 || remainder === 11) {
    remainder = 0;
  }

  if (remainder !== Number(normalizedCpf.charAt(9))) {
    return false;
  }

  sum = 0;
  for (let index = 0; index < 10; index += 1) {
    sum += Number(normalizedCpf.charAt(index)) * (11 - index);
  }

  remainder = 11 - (sum % 11);
  if (remainder === 10 || remainder === 11) {
    remainder = 0;
  }

  return remainder === Number(normalizedCpf.charAt(10));
};

export const hasCpfValue = (cpf?: string): boolean => {
  return (cpf ?? "").replace(/\D/g, "").length > 0;
};

export const hasRgValue = (rg?: string): boolean => {
  return (rg ?? "").replace(/[^0-9A-Za-z]/g, "").length > 0;
};

export const normalizePatientCpf = (cpf?: string): string | null => {
  const trimmedCpf = cpf?.trim() ?? "";
  return hasCpfValue(trimmedCpf) ? trimmedCpf : null;
};

export const normalizePatientRg = (rg?: string): string | null => {
  const trimmedRg = rg?.trim().toUpperCase() ?? "";
  return hasRgValue(trimmedRg) ? trimmedRg : null;
};

const getPatientPhoneDigits = (phone?: string): string => {
  return (phone ?? "").replace(/\D/g, "").slice(0, 11);
};

export const formatPatientPhoneInput = (value: string): string => {
  const digits = getPatientPhoneDigits(value);

  if (digits.length <= 2) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }

  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

export const normalizePatientEmail = (email?: string): string | null => {
  const normalizedEmail = email?.trim().toLowerCase() ?? "";
  return normalizedEmail || null;
};

export const validatePatientEmail = (email?: string): string | null => {
  const normalizedEmail = normalizePatientEmail(email);

  if (!normalizedEmail) {
    return null;
  }

  return EMAIL_REGEX.test(normalizedEmail) ? null : "Email inválido";
};

export const normalizePatientPhone = (phone?: string): string | null => {
  const digits = getPatientPhoneDigits(phone);

  if (!digits) {
    return null;
  }

  return formatPhoneNumber(digits);
};

export const validatePatientPhone = (phone?: string): string | null => {
  const digits = getPatientPhoneDigits(phone);

  if (!digits) {
    return null;
  }

  if (digits.length === 10 || digits.length === 11) {
    return null;
  }

  return "Celular/WhatsApp deve ter 10 ou 11 dígitos";
};

export const validatePatientDocuments = (cpf?: string, rg?: string): string | null => {
  const hasCpf = hasCpfValue(cpf);
  const hasRg = hasRgValue(rg);

  if (!hasCpf && !hasRg) {
    return "Informe CPF ou RG";
  }

  if (hasCpf && !validateCpf(cpf ?? "")) {
    return "CPF inválido";
  }

  return null;
};

export const shouldConfirmLowPhysicalData = (heightCm: number, weightKg: number): boolean => {
  const isHeightLow = heightCm < 50;
  const isWeightLow = weightKg < 20;

  if (!isHeightLow && !isWeightLow) {
    return true;
  }

  const confirmationMessage = [
    "Os dados físicos informados estão abaixo do padrão esperado.",
    "Altura menor que 50 cm ou peso menor que 20 kg.",
    "Confirma que os dados estão corretos?",
  ].join("\n");

  return window.confirm(confirmationMessage);
};

export const calculateIMC = (weightKg: number, heightCm: number): number => {
  if (!weightKg || !heightCm || heightCm === 0) {
    return 0;
  }

  const heightInMeters = heightCm / 100;
  return weightKg / (heightInMeters * heightInMeters);
};

export const getIMCClassification = (imc: number): string => {
  if (imc < 18.5) return "Abaixo do peso";
  if (imc < 25) return "Peso normal";
  if (imc < 30) return "Sobrepeso";
  if (imc < 35) return "Obesidade grau I";
  if (imc < 40) return "Obesidade grau II";
  return "Obesidade grau III";
};
